import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { GITHUB, DISCORD, KEKA, DRIVE } from "icons";
import { EvtHeader, EvtSectionHeader } from "components/header";
import EditItemButton from "components/buttons/edit";
import EntityIntegrationsCard from "components/entity/integrations/display-card";
import { DropdownMenuItem } from "ui/components/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { Button } from "ui/components/button";
import { selectUser } from "store/src/slices/auth-slice";
import { PermissionControlledInput } from "permissions/components/input";
import { StatusDisplay } from "@/components/status/display";
import Loader from "@/components/loader";
import ButtonWithLoading from "@/components/button-with-loading";
import { useFetch } from "@/hooks/useFetch";
import HeaderPortal from "components/portal/header-portal";
import {
  fetchKekaMeta,
  fetchDriveMeta,
  fetchDiscordMeta,
  fetchGithubMeta,
  connectIntegration,
  fetchEntity,
} from "./api";
import { useDeleteEntityIntegration } from "./mutation";
import { toast } from "sonner";
export default function EntityDashboard() {
  const { entityId } = useParams();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [integrationToDelete, setIntegrationToDelete] = useState(null);

  const {
    data: entity,
    isLoading,
    isError,
  } = useFetch({
    queryKey: [`entity-${entityId}`],
    queryFn: () => fetchEntity({ legalEntityId: entityId }),
    enabled: !!entityId,
  });

  const { data: keka, isLoading: isKekaLoading } = useFetch({
    queryKey: [`keka-${entityId}`],
    queryFn: () => fetchKekaMeta({ legalEntityId: entityId }),
    enabled: !!entityId,
  });

  const { data: drive, isLoading: isDriveLoading } = useFetch({
    queryKey: [`drive-${entityId}`],
    queryFn: () => fetchDriveMeta({ legalEntityId: entityId }),
    enabled: !!entityId,
  });

  const { data: discord, isLoading: isDiscordLoading } = useFetch({
    queryKey: [`discord-${entityId}`],
    queryFn: () => fetchDiscordMeta({ legalEntityId: entityId }),
    enabled: !!entityId,
  });

  const { data: github, isLoading: isGithubLoading } = useFetch({
    queryKey: [`github-${entityId}`],
    queryFn: () => fetchGithubMeta({ legalEntityId: entityId }),
    enabled: !!entityId,
  });

  const integrations = [
    {
      id: "github",
      name: "GitHub",
      description: "When connected, PC/JC can add you to the right repo.",
      icon: GITHUB,
      userId: user.id,
    },
    {
      id: "discord",
      name: "Discord",
      description: "When connected, PC/JC can add you to the right channel.",
      icon: DISCORD,
      userId: user.id,
    },
    {
      id: "drive",
      name: "Drive",
      description: "When connected, PC/JC can add you to the right folder.",
      icon: DRIVE,
      userId: user.id,
    },
    {
      id: "keka",
      name: "Keka",
      description: "When connected, Org employee data syncs with Keka.",
      icon: KEKA,
      userId: user.id,
    },
  ];

  const integrationMeta = {
    github,
    discord,
    drive,
    keka,
  };

  const integrationLoading = {
    github: isGithubLoading,
    discord: isDiscordLoading,
    drive: isDriveLoading,
    keka: isKekaLoading,
  };

  const deleteIntegrationMutation = useDeleteEntityIntegration(entityId, {
    onSuccess: () => setIntegrationToDelete(null),
  });

  const handleConnect = async (integrationId) => {
    try {
      if (integrationId === "github") {
        const response = await connectIntegration({
          provider: integrationId,
          legalEntityId: entityId,
        });
        if (response?.install_url) {
          window.location.assign(response.install_url);
          return;
        }
        toast.error("Install URL not found");
        return;
      }
      if (integrationId === "discord" || integrationId === "drive") {
        const response = await connectIntegration({
          provider: integrationId,
          legalEntityId: entityId,
        });
        if (response?.authorize_url) {
          window.location.assign(response.authorize_url);
          return;
        }
        toast.error("Install URL not found");
        return;
      }

      if (integrationId === "keka") {
        navigate(`/organization/entities/${entityId}/keka`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || `Failed to connect ${integrationId}`);
    }
  };

  const getIntegrationRecord = (integrationId) => {
    const records = integrationMeta[integrationId];
    return records?.[0];
  };

  const isIntegrationConnected = (integration) => {
    if (!user) return false;
    const records = integrationMeta[integration.id];
    return Boolean(records?.length);
  };

  const handleConfirmDelete = () => {
    if (!integrationToDelete) return;

    const record = getIntegrationRecord(integrationToDelete.id);

    deleteIntegrationMutation.mutate({
      provider: integrationToDelete.id,
      id: record?.id,
      legalEntityId: entityId,
    });
  };

  const connectedIntegrations = integrations.filter((integration) => isIntegrationConnected(integration),
  );
  const disconnectedIntegrations = integrations.filter(
    (integration) => !isIntegrationConnected(integration),
  );

  if (isLoading || isError) return <Loader />;

  return (
    <div className="flex-1">
      <div className="flex flex-col items-start justify-between">
        <EvtHeader title={entity?.name} />
        <div className="px-6">
          <StatusDisplay value={entity?.status} />
        </div>
      </div>
      <div>
        <PermissionControlledInput
          rule="update:*"
          collection="legal_entities"
          showWhenDisabled={false}
        >
          <div>
            <EvtSectionHeader title="Integrations" />
          </div>
          <section>
            {connectedIntegrations.length > 0 && (
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 xl:grid-cols-3">
                {connectedIntegrations.map((integration) => (
                  <EntityIntegrationsCard
                    key={integration.id}
                    icon={integration.icon}
                    title={integration.name}
                    description={`Your ${integration.name} is successfully connected and syncing.`}
                    isConnected
                    loading={integrationLoading[integration.id]}
                    isDeleting={
                      deleteIntegrationMutation.isPending &&
                      deleteIntegrationMutation.variables?.provider ===
                      integration.id
                    }
                    onDelete={() => setIntegrationToDelete(integration)}
                  />
                ))}
              </div>
            )}
            {disconnectedIntegrations.length > 0 && (<div>
              <EvtSectionHeader
                title="Available Integrations"
                className="!pt-0"
              />
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 xl:grid-cols-3">
                {disconnectedIntegrations.map((integration) => (
                  <EntityIntegrationsCard
                    key={integration.id}
                    icon={integration.icon}
                    title={integration.name}
                    description={integration.description}
                    isConnected={false}
                    loading={integrationLoading[integration.id]}
                    onConnect={() => handleConnect(integration.id)}
                  />
                ))}
              </div>
            </div>
            )}
          </section>
        </PermissionControlledInput>
      </div>
      <HeaderPortal>
        <PermissionControlledInput
          rule="update:*"
          collection="legal_entities"
        >
          <EditItemButton />
        </PermissionControlledInput>
      </HeaderPortal>
      <Dialog
        open={!!integrationToDelete}
        onOpenChange={(open) => {
          if (!open) setIntegrationToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete integration?</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect{" "}
              <strong>{integrationToDelete?.name}</strong>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIntegrationToDelete(null)}
              disabled={deleteIntegrationMutation.isPending}
            >
              Cancel
            </Button>
            <ButtonWithLoading
              variant="destructive"
              isLoading={deleteIntegrationMutation.isPending}
              onClick={handleConfirmDelete}
            >
              Delete
            </ButtonWithLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
